// Closed worlds on the Portal (Ben, Oct 19 2026): tap one to see how you finished and the top five guilds and players.
import { useState } from "react";
import { IconChevronRight, IconClose, IconCrown, IconTrophy } from "@/kit/icons";
import { Sheet } from "@/kit/overlays";
import { Card, CardHead, Disc, Medallion, Row, RowText, StatBand } from "@/kit/primitives";
import { abbrev, fmtNum, plural } from "@/game/format";
import { wonWorld } from "@/game/history";
import { useClosedWorlds, type ClosedWorld, type Standing } from "./useClosedWorlds";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const day = (ms: number) => { const d = new Date(ms); return `${MON[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`; };
const hm = (ms: number) => { const d = new Date(ms); return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")} UTC`; };

export function ClosedWorldsCard() {
  const q = useClosedWorlds();
  const [sel, setSel] = useState<ClosedWorld | null>(null);
  const list = q.data ?? [];
  if (!list.length) return null;
  return (
    <Card>
      <CardHead title="Closed worlds" aside={`${list.length}`} asideCaps />
      <div className="rows">
        {list.map((w) => {
          const won = wonWorld(w);
          const g1 = w.guilds[0];
          return (
            <Row key={w.wid} as="button" className="pw-closed" onClick={() => setSel(w)}>
              <Medallion glow={won ? "ember" : false}>{won ? <IconCrown /> : <IconTrophy />}</Medallion>
              <RowText
                eyebrow={`World ${w.wid}${w.name ? ` · ${w.name}` : ""}`}
                line={<>{w.endedAt > 0 ? `Ended ${day(w.endedAt)}` : "World closed"}{won ? <> · <b>You won</b></> : null}</>}
                meta={<>{w.me.rank > 0 ? <>You finished <b className="gold">#{fmtNum(w.me.rank)}</b> · </> : null}{w.me.points > 0 ? `${abbrev(w.me.points)} pts · ` : ""}{g1 ? <>won by {g1.tag ? `[${g1.tag.toUpperCase()}] ` : ""}{g1.name}</> : "winner not saved"}</>}
              />
              <IconChevronRight className="chev" />
            </Row>
          );
        })}
      </div>
      <Sheet open={!!sel} onClose={() => setSel(null)} label={sel ? `World ${sel.wid} results` : "World results"}>
        {sel && <ClosedWorldSheet w={sel} onClose={() => setSel(null)} />}
      </Sheet>
    </Card>
  );
}

function Board({ title, rows, guild }: { title: string; rows: Standing[]; guild?: boolean }) {
  return (
    <div className="cw-board">
      <div className="pw-sh"><span>{title}</span><span className="aside">Top 5</span></div>
      {rows.slice(0, 5).map((r) => (
        <div key={r.rank} className={["cw-r", r.rank === 1 && "first", r.you && "you"].filter(Boolean).join(" ")}>
          <span className="cw-rank">{r.rank === 1 ? <IconCrown /> : r.rank}</span>
          <span className="cw-name">
            {guild && r.tag ? <><span className="cw-tag">[{r.tag.toUpperCase()}]</span> {r.name}</> : r.name}
            {r.you && <span className="cw-you">You</span>}
            <small>{!guild && r.tag ? `[${r.tag.toUpperCase()}] · ` : ""}{plural(r.cities, "city", "cities")}</small>
          </span>
          <span className="cw-pts">{abbrev(r.points)}</span>
        </div>
      ))}
    </div>
  );
}

function ClosedWorldSheet({ w, onClose }: { w: ClosedWorld; onClose: () => void }) {
  const m = w.me;
  return (
    <div className="cw">
      <div className="cw-head">
        <div>
          <div className="eyebrow">World {w.wid}{w.name ? ` · ${w.name}` : ""}</div>
          <h3>Final standings</h3>
          {w.endedAt > 0 && <div className="cw-sub">Ended {day(w.endedAt)}</div>}
        </div>
        <Disc size="sm" label="Close" onClick={onClose}><IconClose /></Disc>
      </div>

      <div className="pw-sh"><span>Your result</span>{m.guild && <span className="aside">[{m.gtag?.toUpperCase()}] {m.guild}{m.guildRank ? ` · guild #${m.guildRank}` : ""}</span>}</div>
      <StatBand stats={[
        { label: "Rank", value: m.rank > 0 ? `#${fmtNum(m.rank)}` : "—" },
        { label: "Points", value: abbrev(m.points) },
        { label: "Cities", value: fmtNum(m.cities) },
      ]} />
      <StatBand stats={[
        { label: "Attack", value: abbrev(m.ksa), small: m.rankKsa ? `#${m.rankKsa}` : undefined },
        { label: "Defense", value: abbrev(m.ksd), small: m.rankKsd ? `#${m.rankKsd}` : undefined },
        { label: "Total kills", value: abbrev(m.kst), small: m.rankKst ? `#${m.rankKst}` : undefined },
      ]} />

      {w.guilds.length > 0 && <Board title="Guilds" rows={w.guilds} guild />}
      {w.players.length > 0 && <Board title="Players" rows={w.players} />}
      {!w.guilds.length && !w.players.length && <p className="cw-foot">The final standings weren't saved for this world.</p>}

      {w.savedAt > 0 && <p className="cw-foot">Standings as last saved, {day(w.savedAt)} at {hm(w.savedAt)}{w.source === "vt" ? " (from Valor Tools)" : ""}.</p>}
    </div>
  );
}
